import type {
  FileUploadRestrictions,
  UploaderMeta,
  UploadSuccessResult,
} from '../types';
import type { UploaderContentOptions } from './UploaderContent';
import type Uppy from '@uppy/core';
import type { Meta } from '@uppy/core';

import { Dialog, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '~/atoms';
import { Button } from '~/molecules';

import { useIsUploaderBusy } from '../utils/useIsUploaderBusy';

import { UploaderContent } from './UploaderContent';

type P<TMeta extends UploaderMeta> = {
  dialogTitle: string;
  errorMessage: string | null;
  isConfirming: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: UploadSuccessResult<TMeta>) => Promise<void>;
  onUploadError: (error: unknown) => void;
  restrictions?: FileUploadRestrictions;
  uppy: Uppy<Meta, Record<string, never>>;
} & UploaderContentOptions<TMeta>;

export function UploaderDialog<TMeta extends UploaderMeta>({
  confirmAfterUpload,
  confirmButtonText,
  dialogTitle,
  errorMessage,
  hintText,
  isConfirming,
  isFileReady,
  isOpen,
  onClose,
  onSuccess,
  onUploadError,
  renderFileList,
  restrictions,
  showRemoveAllFiles,
  showRestrictionsHint,
  uploadButtonText,
  uppy,
}: P<TMeta>) {
  const { t } = useTranslation('ui');
  const isBusy = useIsUploaderBusy(uppy, isConfirming);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={isBusy ? undefined : onClose}
      open={isOpen}
    >
      <header className="flex items-center justify-between gap-4 px-4">
        <DialogTitle
          id="uploader-dialog-title"
          className="min-w-0 truncate px-0 py-4"
        >
          {dialogTitle}
        </DialogTitle>
        <Button
          aria-label={t(($) => $.button.secondary.default)}
          buttonStyle="link"
          className="-mt-1 -mr-1 shrink-0 text-tiga-text-lighter hover:text-tiga-action-lighter"
          disabled={isBusy}
          icon={<Icon metaphor="close" />}
          onClick={onClose}
        />
      </header>

      <UploaderContent
        className="px-4"
        confirmAfterUpload={confirmAfterUpload}
        confirmButtonText={confirmButtonText}
        errorMessage={errorMessage}
        hintText={hintText}
        isConfirming={isConfirming}
        isFileReady={isFileReady}
        onSuccess={onSuccess}
        onUploadError={onUploadError}
        renderFileList={renderFileList}
        restrictions={restrictions}
        showRemoveAllFiles={showRemoveAllFiles}
        showRestrictionsHint={showRestrictionsHint}
        uploadButtonText={uploadButtonText}
        uppy={uppy}
      />
    </Dialog>
  );
}
